import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, BookmarkPlus, Check, ChevronDown, ChevronLeft, ChevronRight, Clipboard, Copy, Download, Expand, FileText, Frame, Heart, ImagePlus, Info, LoaderCircle, PanelLeft, PanelLeftClose, PanelRight, PanelRightClose, PencilLine, Plus, RefreshCw, Sparkles, Trash2, Upload, X, ZoomIn, ZoomOut, } from 'lucide-react';
import { DELETE_ROUTE, SAVE_WORKSPACE_ROUTE, STUDIO_ROUTE, isSubscriptionProvider } from '../shared.js';
import { deleteGalleryItem, getGalleryItems, saveGalleryItem, subscribeGallery, toggleFavoriteGalleryItem } from './gallery-store.js';
import { evictAttachmentCache, fetchAttachmentBlob } from './image-cache.js';
import { copyImageBlob, downloadBlobUrl, formatRelativeTime } from './browser-image-utils.js';
import { buildComparisonTargets, initialComparisonProviders } from './multi-model-compare.js';
import { clearStudioTlCanvases, StudioTlCanvas } from './tl/studio-tl-canvas.js';
import { pushTlLandings } from './tl/tl-canvas-bridge.js';
const PAGE_SIZE = 12;
/** Below this workbench width the recent-generations rail folds automatically. */
const WORKBENCH_NARROW_WIDTH = 560;
const COPY = {
    zh: {
        title: '云端生图工作台', configured: 'API 已配置', unconfigured: '未配置', recent: '最近生成', empty: '暂无生成历史',
        generate: '文生图', edit: '图生图', reference: '参考图', optional: '选填', upload: '点击或拖拽图片到此处',
        uploadHint: '支持 JPG / PNG / WebP / GIF，最大 10MB（最多 5 张）', prompt: '提示词 Prompt', clear: '清空', promptPlaceholder: '描述主体、构图、风格、光线与需要出现的文字…（支持 Ctrl+Enter 快捷生成）',
        provider: 'Provider', model: 'Model', ratio: '比例', quality: '清晰度', start: '开始生成', generating: '正在生成…', cancelGenerate: '取消生成',
        count: '生成数量', countUnit: '{n} 张', partialSuccess: '已生成 {success} 张图片，{failed} 张失败', generatingCount: '正在生成（共 {count} 张）…',
        batchResult: '本次生成（共 {count} 张）',
        singleModel: '单模型', compareModels: '多模型对比', compareHint: '相同提示词，同时交给多个模型', compareSelect: '选择模型', compareSelected: '已选 {count} 个模型',
        compareNeedTwo: '请至少选择两个已配置模型', compareParameterHint: '不支持所选参数的模型会自动使用默认值', compareAdjusted: '已适配', comparePartial: '{success} 个模型生成成功，{failed} 个失败',
        comparing: '正在对比（共 {count} 个模型）…', compareStart: '开始对比',
        saveToGallery: '保存', saveSelected: '保存({count})', savedToGallery: '已保存到画廊', savedSelected: '已保存 {count} 张图片', inGallery: '已保存', needSelectResult: '请先勾选要保存的图片',
        retry: '重新加载', configLoadFailed: '工作台配置加载失败，请检查服务后重试。',
        noProvider: '请先在设置中配置至少一个云端图像 Provider 的 API Key。', selectConfigured: '该 Provider 尚未配置，请先到设置中配置 API Key。',
        needPrompt: '请输入提示词', needReference: '请先添加至少一张参考图', result: '本次结果', continueEdit: '继续编辑（垫图）', regenerate: '再次生成',
        copy: '复制', copied: '已复制到剪贴板', copyFailed: '复制失败', download: '下载', remove: '删除', fit: '适应窗口', infiniteCanvas: '无限画布', loading: '正在读取图片…', generationFailed: '生成失败',
        selectHistory: '从左侧选择一张图片，或在右侧开始新的生成。', created: '生成时间', elapsed: '耗时', dimensions: '尺寸', output: '输出参数',
        closeReference: '移除参考图', uploadInvalid: '请选择有效的图片文件（最大 10MB）', imageLoadFailed: '图片读取失败',
        fullscreen: '大图全屏', close: '关闭', copyPpt: '复制 Prompt', copiedPrompt: '已复制 Prompt', copiedImage: '已复制图片',
        favorite: '收藏', favorited: '已收藏', favoriteAdded: '已添加到收藏', favoriteRemoved: '已取消收藏', gallerySaveFailed: '保存到图库失败，请重试',
        loadMore: '加载更多 ({n})', deleteModalTitle: '从图库删除', deleteModalDesc: '确定从图库中删除这张图片吗？（原聊天记录不会受影响）',
        deleteWorkspaceFilesLabel: '同时删除工作区本地文件', confirm: '确定删除', cancel: '取消', deletedToast: '已从图库删除',
        referencesCount: '参考图 ({current}/{max})', addMoreRef: '+ 添加', maxReferencesExceeded: '最多支持添加 {max} 张参考图',
        newGeneration: '新建生成', new: '新建',
        configuredCount: 'API 已配置 · {count}', unconfiguredStatus: 'API 未配置', providerStatus: '云端提供商与模型状态',
        configuredSplit: 'API {api} · 订阅 {sub}', subSignedIn: '已登录', subSignedOut: '未登录',
        collapseSidebar: '折叠最近生成', expandSidebar: '展开最近生成',
        collapseGenerate: '折叠生成面板', expandGenerate: '展开生成面板', generatePanel: '生成面板',
        findInspiration: '找灵感',
        infiniteCanvasHint: '无限画布与侧边栏会话联动，才是完整功能。打开 DSH 右侧边栏的「图像工作台」即可解锁。',
        clearCanvas: '清空画布', clearCanvasDescription: '将永久清空所有画布页面的内容、图片资产和撤销历史。画廊已保存的卡片及原图不受影响，此操作无法撤销。', canvasCleared: '画布已清空，画廊不受影响',
    },
    en: {
        title: 'Cloud Image Studio', configured: 'API configured', unconfigured: 'Not configured', recent: 'Recent generations', empty: 'No generated images yet',
        generate: 'Text to image', edit: 'Image to image', reference: 'Reference image', optional: 'optional', upload: 'Click or drop images here',
        uploadHint: 'JPG / PNG / WebP / GIF, up to 10MB (max 5)', prompt: 'Prompt', clear: 'Clear', promptPlaceholder: 'Describe the subject, composition, style, lighting, and exact text… (Ctrl+Enter to generate)',
        provider: 'Provider', model: 'Model', ratio: 'Aspect ratio', quality: 'Quality', start: 'Generate', generating: 'Generating…', cancelGenerate: 'Cancel',
        count: 'Number of images', countUnit: '{n}', partialSuccess: 'Generated {success} images, {failed} failed', generatingCount: 'Generating ({count} images)…',
        batchResult: 'Generated {count} images',
        singleModel: 'Single model', compareModels: 'Compare models', compareHint: 'Send the same prompt to multiple models', compareSelect: 'Choose models', compareSelected: '{count} models selected',
        compareNeedTwo: 'Select at least two configured models', compareParameterHint: 'Unsupported settings use each model’s default', compareAdjusted: 'Adjusted', comparePartial: '{success} models succeeded, {failed} failed',
        comparing: 'Comparing {count} models…', compareStart: 'Compare models',
        saveToGallery: 'Save to Gallery', saveSelected: 'Save selected ({count})', savedToGallery: 'Saved to Gallery', savedSelected: 'Saved {count} images', inGallery: 'In Gallery', needSelectResult: 'Select images to save first',
        retry: 'Retry', configLoadFailed: 'Failed to load studio configuration.',
        noProvider: 'Configure an API key for at least one cloud image provider in Settings.', selectConfigured: 'This provider is not configured. Add its API key in Settings first.',
        needPrompt: 'Enter a prompt', needReference: 'Add at least one reference image first', result: 'Current result', continueEdit: 'Continue editing', regenerate: 'Generate again',
        copy: 'Copy', copied: 'Copied to clipboard', copyFailed: 'Copy failed', download: 'Download', remove: 'Delete', fit: 'Fit', infiniteCanvas: 'Infinite canvas', loading: 'Loading image…', generationFailed: 'Generation failed',
        selectHistory: 'Select an image on the left, or start a new generation on the right.', created: 'Created', elapsed: 'Elapsed', dimensions: 'Dimensions', output: 'Output',
        closeReference: 'Remove reference', uploadInvalid: 'Choose a valid image file up to 10MB.', imageLoadFailed: 'Could not load image',
        fullscreen: 'Fullscreen', close: 'Close', copyPpt: 'Copy Prompt', copiedPrompt: 'Prompt copied', copiedImage: 'Image copied',
        favorite: 'Favorite', favorited: 'Favorited', favoriteAdded: 'Added to favorites', favoriteRemoved: 'Removed from favorites', gallerySaveFailed: 'Failed to save to the gallery, please retry',
        loadMore: 'Load more ({n})', deleteModalTitle: 'Delete from gallery', deleteModalDesc: 'Remove this image from the local gallery? (Chat history remains unaffected)',
        deleteWorkspaceFilesLabel: 'Also delete local workspace files', confirm: 'Delete', cancel: 'Cancel', deletedToast: 'Deleted from gallery',
        referencesCount: 'References ({current}/{max})', addMoreRef: '+ Add', maxReferencesExceeded: 'Up to {max} reference images allowed',
        newGeneration: 'New generation', new: 'New',
        configuredCount: 'API configured · {count}', unconfiguredStatus: 'Not configured', providerStatus: 'Provider & Model Status',
        configuredSplit: 'API {api} · Sub {sub}', subSignedIn: 'Signed in', subSignedOut: 'Signed out',
        collapseSidebar: 'Collapse sidebar', expandSidebar: 'Expand recent list',
        collapseGenerate: 'Collapse generate panel', expandGenerate: 'Expand generate panel', generatePanel: 'Generate',
        findInspiration: 'Find inspiration',
        infiniteCanvasHint: 'The infinite canvas is at its best when linked with the sidebar conversation. Open Image Studio from the DSH right sidebar to unlock the full experience.',
        clearCanvas: 'Clear canvas', clearCanvasDescription: 'Permanently clear all canvas pages, image assets, and undo history. Saved gallery cards and original images are unaffected. This cannot be undone.', canvasCleared: 'Canvas cleared. Gallery unchanged.',
    },
};
/** localStorage flag remembering that the one-time infinite-canvas hint was dismissed. */
const INFINITE_CANVAS_HINT_KEY = 'dsh-ig-infinite-canvas-hint-dismissed';
export const StudioView = ({ locale, credentialEvents, workspace, initialPrompt, initialCanvasSurface, showInfiniteCanvasHint, onInitialPromptApplied, onOpenInspiration }) => {
    const [lang, setLang] = useState(() => locale?.getSnapshot?.().active?.startsWith('en') ? 'en' : 'zh');
    const [config, setConfig] = useState(null);
    const [configLoading, setConfigLoading] = useState(true);
    const [configError, setConfigError] = useState(null);
    const [items, setItems] = useState([]);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    /** Manual-only collapse for the right generate form; the form stays put in
     *  narrow seats because it is the primary generation surface. Starts folded
     *  when the studio opens straight into the infinite canvas. */
    const [generateCollapsed, setGenerateCollapsed] = useState(initialCanvasSurface === 'infinite');
    /** Workbench root, observed so a narrow column (right sidebar) can fold the recent rail. */
    const workbenchRootRef = useRef(null);
    /** Set once the user toggles the rail by hand; auto-folding then stands down for this mount. */
    const railToggledByUserRef = useRef(false);
    const [visibleLimit, setVisibleLimit] = useState(30);
    const [selected, setSelected] = useState(null);
    const [mode, setMode] = useState('generate');
    const [provider, setProvider] = useState('google');
    const [model, setModel] = useState('');
    const [ratio, setRatio] = useState('1:1');
    const [quality, setQuality] = useState('1K');
    const [prompt, setPrompt] = useState('');
    const [references, setReferences] = useState([]);
    const [count, setCount] = useState(1);
    const [currentBatch, setCurrentBatch] = useState(null);
    const [selectedBatchIds, setSelectedBatchIds] = useState([]);
    const [batchKind, setBatchKind] = useState(null);
    const [comparisonEnabled, setComparisonEnabled] = useState(false);
    const [comparisonProviders, setComparisonProviders] = useState([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    // Interactive Pan-Zoom Viewport State
    const [zoom, setZoom] = useState(100);
    const [fit, setFit] = useState(true);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const dragStartRef = useRef(null);
    const hasDraggedRef = useRef(false);
    // Canvas surface: 'preview' keeps the single-image viewer (fake canvas),
    // 'infinite' mounts the tldraw editor. Defaults to 'preview' so existing
    // behaviour is untouched until the toggle is clicked; a caller may open the
    // infinite canvas directly (the right-sidebar studio variant does).
    const [canvasSurface, setCanvasSurface] = useState(initialCanvasSurface ?? 'preview');
    const [showClearCanvasModal, setShowClearCanvasModal] = useState(false);
    useEffect(() => {
        if (!showClearCanvasModal)
            return;
        const close = (event) => {
            if (event.key === 'Escape')
                setShowClearCanvasModal(false);
        };
        window.addEventListener('keydown', close);
        return () => window.removeEventListener('keydown', close);
    }, [showClearCanvasModal]);
    /** Generate-panel state from before the infinite canvas was opened, so the
     *  panel is restored (not just forced open) when returning to preview. */
    const generatePanelBeforeInfiniteRef = useRef(false);
    /** Mirrors `canvasSurface` for async reads: a generation started on the
     *  infinite canvas must land there even if the user flips back to preview
     *  while the request is in flight. */
    const canvasSurfaceRef = useRef(canvasSurface);
    canvasSurfaceRef.current = canvasSurface;
    /** One-time infinite-canvas entry hint, dismissed per-install (localStorage)
     *  so it educates once instead of nagging on every visit. */
    const [canvasHintDismissed, setCanvasHintDismissed] = useState(() => {
        try {
            return localStorage.getItem(INFINITE_CANVAS_HINT_KEY) === 'true';
        }
        catch {
            return false;
        }
    });
    const dismissCanvasHint = useCallback(() => {
        setCanvasHintDismissed(true);
        try {
            localStorage.setItem(INFINITE_CANVAS_HINT_KEY, 'true');
        }
        catch { }
    }, []);
    const [dragging, setDragging] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteWorkspaceFiles, setDeleteWorkspaceFiles] = useState(true);
    const requestRef = useRef(null);
    const canvasRef = useRef(null);
    const fileInputRef = useRef(null);
    const image = useAttachmentImage(selected?.attachment);
    useEffect(() => {
        if (initialPrompt === undefined)
            return;
        setMode('generate');
        setPrompt(initialPrompt);
        onInitialPromptApplied?.();
    }, [initialPrompt, onInitialPromptApplied]);
    // Narrow-column auto-fold: the right-sidebar seat is far narrower than the
    // conversation-view tab, and the form + canvas need the width more than the
    // recent rail does. Auto mode stands down after the first manual toggle.
    useEffect(() => {
        const root = workbenchRootRef.current;
        if (root === null || typeof ResizeObserver === 'undefined')
            return;
        const observer = new ResizeObserver((entries) => {
            if (railToggledByUserRef.current)
                return;
            for (const entry of entries) {
                const narrow = entry.contentRect.width < WORKBENCH_NARROW_WIDTH;
                setSidebarCollapsed(prev => (prev === narrow ? prev : narrow));
            }
        });
        observer.observe(root);
        return () => { observer.disconnect(); };
    }, []);
    const maxReferences = comparisonEnabled
        ? (comparisonProviders.includes('dashscope') ? 3 : 5)
        : (provider === 'dashscope' ? 3 : 5);
    const referencesRef = useRef(references);
    referencesRef.current = references;
    // Parameter locking for the comparison view: only when every selected model
    // is a subscription channel (channel-default parameters only) do the shared
    // ratio/quality controls step aside; a mixed selection keeps user-chosen
    // parameters for the API rows while subscription rows use channel defaults.
    const comparisonSelectionsAllSubscription = comparisonEnabled
        && comparisonProviders.length > 0
        && comparisonProviders.every(provider => isSubscriptionProvider(provider));
    // Single-model view: a subscription provider has no parameter matrix at all.
    const singleProviderIsSubscription = !comparisonEnabled && isSubscriptionProvider(provider);
    const t = (key, values) => {
        let text = COPY[lang][key];
        for (const [name, value] of Object.entries(values ?? {}))
            text = text.replace(`{${name}}`, value);
        return text;
    };
    useEffect(() => locale?.subscribe?.(() => setLang(locale.getSnapshot().active?.startsWith('en') ? 'en' : 'zh')), [locale]);
    // Latest form state for the silent refresher, kept in refs so loadConfig's
    // identity stays stable and a refresh never re-runs the initial load.
    const formStateRef = useRef({ provider, model });
    formStateRef.current = { provider, model };
    const loadConfig = useCallback(async (options) => {
        const silent = options?.silent === true;
        if (!silent) {
            setConfigLoading(true);
            setConfigError(null);
        }
        const controller = new AbortController();
        try {
            const response = await fetch(STUDIO_ROUTE, { signal: controller.signal, credentials: 'same-origin' });
            const payload = await response.json();
            if (!response.ok || !('providers' in payload))
                throw new Error('error' in payload && payload.error ? payload.error : 'Studio unavailable');
            setConfig(payload);
            if (silent) {
                // Silent refresh: keep the user's in-flight form state (prompt,
                // parameters, selections). Only reconcile the selected provider's
                // model when it changed on the host, and drop comparisons that are
                // no longer signed in / configured.
                const { provider: currentProvider, model: currentModel } = formStateRef.current;
                const activeProfile = payload.providers.find(item => item.provider === currentProvider);
                if (activeProfile !== undefined && activeProfile.model !== currentModel)
                    setModel(activeProfile.model);
                setComparisonProviders(current => current.filter(selectedProvider => payload.providers.some(item => item.provider === selectedProvider && item.configured)));
                return;
            }
            const initial = payload.providers.find(item => item.provider === payload.activeProvider) ?? payload.providers[0];
            if (initial !== undefined) {
                applyProvider(initial);
                setComparisonProviders(initialComparisonProviders(payload.providers, initial.provider));
            }
        }
        catch (fetchError) {
            if (!silent && !controller.signal.aborted)
                setConfigError(messageOf(fetchError));
        }
        finally {
            if (!silent)
                setConfigLoading(false);
        }
    }, []);
    useEffect(() => {
        void loadConfig();
    }, [loadConfig]);
    // Host credential changes (subscription sign-in/out, API keys) re-read the
    // provider state silently; the workbench refreshes badges and option lists
    // without touching the prompt or parameters the user is working on. The
    // one-second delay lets the host finish persisting the new credential.
    useEffect(() => {
        if (credentialEvents === undefined)
            return;
        return credentialEvents.listen(() => {
            window.setTimeout(() => { void loadConfig({ silent: true }); }, 1_000);
        });
    }, [credentialEvents, loadConfig]);
    useEffect(() => {
        let mounted = true;
        const load = () => void getGalleryItems().then(next => {
            if (!mounted)
                return;
            setItems(next);
            setSelected(current => current === null ? null : (next.find(item => item.id === current.id) ?? null));
        });
        load();
        const unsubscribe = subscribeGallery(load);
        return () => { mounted = false; unsubscribe(); };
    }, []);
    // Abort only on component unmount
    useEffect(() => {
        return () => {
            requestRef.current?.abort();
        };
    }, []);
    // Revoke all reference object URLs on component unmount
    useEffect(() => {
        return () => {
            for (const item of referencesRef.current) {
                if (item.previewUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(item.previewUrl);
                }
            }
        };
    }, []);
    // Support Escape to close Lightbox
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && lightboxOpen) {
                setLightboxOpen(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [lightboxOpen]);
    // Non-passive wheel zoom on canvas to prevent page scrolling and provide smooth zooming
    useEffect(() => {
        const canvasEl = canvasRef.current;
        if (!canvasEl)
            return;
        const onWheel = (e) => {
            if (selected === null && (!currentBatch || currentBatch.length === 0))
                return;
            e.preventDefault();
            const delta = e.deltaY < 0 ? 15 : -15;
            setFit(false);
            setZoom(prev => Math.min(500, Math.max(25, prev + delta)));
        };
        canvasEl.addEventListener('wheel', onWheel, { passive: false });
        return () => canvasEl.removeEventListener('wheel', onWheel);
    }, [selected, currentBatch]);
    // Global mousemove and mouseup to handle canvas panning smoothly
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!dragStartRef.current)
                return;
            const dx = e.clientX - dragStartRef.current.x;
            const dy = e.clientY - dragStartRef.current.y;
            if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
                hasDraggedRef.current = true;
                setFit(false);
            }
            setOffset({
                x: dragStartRef.current.startX + dx,
                y: dragStartRef.current.startY + dy,
            });
        };
        const handleMouseUp = () => {
            dragStartRef.current = null;
            setIsDragging(false);
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);
    const activeProfile = useMemo(() => config?.providers.find(item => item.provider === provider), [config, provider]);
    const configuredCount = config?.providers.filter(item => item.configured).length ?? 0;
    // Split badge counts: BYOK keys and subscription sign-ins stay separately
    // visible so a number is never a mixed bag of semantics.
    const apiConfiguredCount = config?.providers.filter(item => item.configured && !isSubscriptionProvider(item.provider)).length ?? 0;
    const subscriptionSignedInCount = config?.providers.filter(item => item.configured && isSubscriptionProvider(item.provider)).length ?? 0;
    const displayItems = useMemo(() => items.slice(0, visibleLimit), [items, visibleLimit]);
    const comparisonProfiles = useMemo(() => (config?.providers ?? []).filter(item => item.configured && (mode === 'generate' || item.supportsEditing)), [config, mode]);
    const comparisonTargets = useMemo(() => buildComparisonTargets(comparisonProfiles, comparisonProviders, ratio, quality), [comparisonProfiles, comparisonProviders, ratio, quality]);
    const applyProvider = (profile) => {
        setProvider(profile.provider);
        setModel(profile.model);
        setRatio(profile.defaultRatio);
        setQuality(profile.defaultQuality);
        setError(null);
    };
    const changeProvider = (value) => {
        const profile = config?.providers.find(item => item.provider === value);
        if (profile !== undefined) {
            applyProvider(profile);
            const providerMax = value === 'dashscope' ? 3 : 5;
            if (referencesRef.current.length > providerMax) {
                const keep = referencesRef.current.slice(0, providerMax);
                const overflow = referencesRef.current.slice(providerMax);
                for (const item of overflow) {
                    if (item.previewUrl.startsWith('blob:'))
                        URL.revokeObjectURL(item.previewUrl);
                }
                setReferences(keep);
                referencesRef.current = keep;
                setError(t('maxReferencesExceeded', { max: String(providerMax) }));
            }
        }
    };
    const resetFit = () => {
        setFit(true);
        setZoom(100);
        setOffset({ x: 0, y: 0 });
    };
    const selectItem = (item) => {
        setCurrentBatch(null);
        setSelectedBatchIds([]);
        setBatchKind(null);
        setSelected(item);
        // No tab switch: the middle canvas shows the selection; the right panel
        // stays on whatever the user is working in (form or chat).
        resetFit();
    };
    const startNew = () => {
        setCurrentBatch(null);
        setSelectedBatchIds([]);
        setBatchKind(null);
        setSelected(null);
        resetFit();
    };
    const handleCanvasMouseDown = (e) => {
        if (e.button !== 0 && e.button !== 1)
            return;
        if (selected === null && (!currentBatch || currentBatch.length === 0))
            return;
        setIsDragging(true);
        hasDraggedRef.current = false;
        dragStartRef.current = {
            x: e.clientX,
            y: e.clientY,
            startX: offset.x,
            startY: offset.y,
        };
    };
    const handleImageClick = () => {
        // Canvas click is for selection, not preview
    };
    const addReferenceFiles = (fileList) => {
        if (!fileList || fileList.length === 0)
            return;
        const files = Array.from(fileList);
        const validFiles = [];
        for (const file of files) {
            if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type) || file.size > 10 * 1024 * 1024) {
                setError(t('uploadInvalid'));
                continue;
            }
            validFiles.push(file);
        }
        if (validFiles.length === 0)
            return;
        const currentLen = referencesRef.current.length;
        const available = maxReferences - currentLen;
        if (available <= 0) {
            setError(t('maxReferencesExceeded', { max: String(maxReferences) }));
            return;
        }
        const toAdd = validFiles.slice(0, available);
        if (validFiles.length > available) {
            setError(t('maxReferencesExceeded', { max: String(maxReferences) }));
        }
        else {
            setError(null);
        }
        const newItems = toAdd.map((file, idx) => ({
            id: `upload-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 7)}`,
            file,
            previewUrl: URL.createObjectURL(file),
        }));
        setReferences(prev => [...prev, ...newItems]);
    };
    const removeReference = (id) => {
        const target = referencesRef.current.find(item => item.id === id);
        if (target && target.previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(target.previewUrl);
        }
        setReferences(prev => prev.filter(item => item.id !== id));
    };
    const clearAllReferences = () => {
        for (const item of referencesRef.current) {
            if (item.previewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(item.previewUrl);
            }
        }
        setReferences([]);
    };
    const continueEdit = async () => {
        if (selected === null)
            return;
        const targetItem = selected;
        const targetAttId = targetItem.attachment.attachmentId;
        const targetMax = targetItem.provider === 'dashscope' ? 3 : 5;
        if (referencesRef.current.some(r => r.attachment?.attachmentId === targetAttId)) {
            setMode('edit');
            changeProvider(targetItem.provider);
            return;
        }
        if (referencesRef.current.length >= targetMax) {
            setError(t('maxReferencesExceeded', { max: String(targetMax) }));
            return;
        }
        try {
            const blob = image.blob ?? await fetchAttachmentBlob(targetItem.attachment);
            if (referencesRef.current.some(r => r.attachment?.attachmentId === targetAttId)) {
                setMode('edit');
                changeProvider(targetItem.provider);
                return;
            }
            if (referencesRef.current.length >= targetMax) {
                setError(t('maxReferencesExceeded', { max: String(targetMax) }));
                return;
            }
            const independentUrl = URL.createObjectURL(blob);
            const newItem = {
                id: `item-${targetItem.id}-${Date.now()}`,
                attachment: targetItem.attachment,
                previewUrl: independentUrl,
            };
            const nextList = [...referencesRef.current, newItem];
            setReferences(nextList);
            referencesRef.current = nextList;
            setError(null);
            setMode('edit');
            changeProvider(targetItem.provider);
        }
        catch {
            setError(t('imageLoadFailed'));
        }
    };
    const cancelGeneration = () => {
        if (requestRef.current) {
            requestRef.current.abort();
            requestRef.current = null;
            setIsGenerating(false);
        }
    };
    const toggleComparison = (enabled) => {
        setComparisonEnabled(enabled);
        setError(null);
        if (!enabled) {
            if (activeProfile !== undefined) {
                if (!activeProfile.ratioOptions.some(option => option.value === ratio))
                    setRatio(activeProfile.defaultRatio);
                if (!activeProfile.qualityOptions.some(option => option.value === quality))
                    setQuality(activeProfile.defaultQuality);
            }
            return;
        }
        if (config === null)
            return;
        const initial = initialComparisonProviders(comparisonProfiles, provider)
            .filter(item => mode !== 'edit' || referencesRef.current.length <= 3 || item !== 'dashscope');
        setComparisonProviders(initial);
        if (!['1:1', '3:2', '2:3', '16:9', '9:16'].includes(ratio))
            setRatio('1:1');
        if (!['standard', '1K', '2K', '4K'].includes(quality))
            setQuality('1K');
    };
    const toggleComparisonProvider = (target) => {
        if (mode === 'edit' && target === 'dashscope' && referencesRef.current.length > 3 && !comparisonProviders.includes(target)) {
            setError(t('maxReferencesExceeded', { max: '3' }));
            return;
        }
        setComparisonProviders(current => current.includes(target)
            ? current.filter(item => item !== target)
            : [...current, target]);
        setError(null);
    };
    const submit = async () => {
        if (isGenerating || activeProfile === undefined)
            return;
        if (prompt.trim().length === 0)
            return setError(t('needPrompt'));
        if (comparisonEnabled && comparisonTargets.length < 2)
            return setError(t('compareNeedTwo'));
        if (!comparisonEnabled && !activeProfile.configured)
            return setError(t('selectConfigured'));
        if (mode === 'edit' && references.length === 0)
            return setError(t('needReference'));
        // Landing rule: capture the active surface at request start. The user may
        // flip between preview and the infinite canvas while the request is in
        // flight; where they were working when they hit generate decides whether
        // results land on the canvas directly (preview trials wait for a save).
        const startedOnInfinite = canvasSurfaceRef.current === 'infinite';
        setIsGenerating(true);
        setError(null);
        const controller = new AbortController();
        requestRef.current = controller;
        try {
            const referencesPayload = mode === 'edit' && references.length > 0
                ? await Promise.all(references.map(async (ref) => {
                    if (ref.attachment !== undefined) {
                        return { attachment: ref.attachment };
                    }
                    if (ref.file !== undefined) {
                        return {
                            mediaType: ref.file.type,
                            data: await fileToBase64(ref.file),
                            ...(ref.file.name ? { name: ref.file.name } : {}),
                        };
                    }
                    throw new Error('无效参考图');
                }))
                : undefined;
            if (comparisonEnabled) {
                const settled = await Promise.allSettled(comparisonTargets.map(async (target) => {
                    const response = await fetch(STUDIO_ROUTE, {
                        method: 'POST', credentials: 'same-origin', signal: controller.signal,
                        headers: { 'content-type': 'application/json' },
                        body: JSON.stringify({
                            mode,
                            provider: target.profile.provider,
                            model: target.profile.model,
                            prompt: prompt.trim(),
                            ratio: target.ratio,
                            quality: target.quality,
                            ...(workspace?.path ? { workspaceRoot: workspace.path } : {}),
                            ...(referencesPayload === undefined ? {} : { references: referencesPayload }),
                        }),
                    });
                    const payload = await response.json();
                    if (!response.ok || !('attachment' in payload)) {
                        throw new Error('error' in payload && payload.error ? payload.error : `${target.profile.label}: ${t('generationFailed')}`);
                    }
                    return { payload, target };
                }));
                if (controller.signal.aborted)
                    return;
                const successes = settled.filter(result => result.status === 'fulfilled');
                const failed = settled.length - successes.length;
                if (successes.length === 0) {
                    const firstFailure = settled.find(result => result.status === 'rejected');
                    throw firstFailure?.status === 'rejected' ? firstFailure.reason : new Error(t('generationFailed'));
                }
                const galleryEntries = successes.map(({ value }, idx) => ({
                    id: String(value.payload.attachment.attachmentId || `${value.payload.createdAt}-${idx}`),
                    attachment: value.payload.attachment,
                    prompt: value.payload.prompt,
                    provider: value.payload.provider,
                    model: value.payload.model,
                    createdAt: value.payload.createdAt + idx,
                    aspectRatio: value.target.ratio,
                    imageSize: value.target.quality,
                    output: value.payload.output,
                    ...(value.payload.savedTo ? { savedTo: value.payload.savedTo } : {}),
                    ...(workspace?.path ? { workspacePath: workspace.path } : {}),
                    ...(workspace?.workspaceId ? { workspaceId: workspace.workspaceId } : {}),
                }));
                setCurrentBatch(galleryEntries.length > 1 ? galleryEntries : null);
                setSelectedBatchIds(galleryEntries.length > 1 ? [galleryEntries[0].id] : []);
                setBatchKind(galleryEntries.length > 1 ? 'multi-model' : null);
                setSelected(galleryEntries[0]);
                resetFit();
                // Mirror the comparison batch onto the tldraw infinite canvas, but
                // only when the user was working there when the request started (see
                // the single-generation path).
                if (startedOnInfinite) {
                    pushTlLandings(galleryEntries.map(entry => ({
                        galleryId: entry.id,
                        attachment: entry.attachment,
                        prompt: entry.prompt,
                        provider: entry.provider,
                        model: entry.model,
                    })));
                }
                if (failed > 0)
                    flash(t('comparePartial', { success: String(successes.length), failed: String(failed) }));
                return;
            }
            const response = await fetch(STUDIO_ROUTE, {
                method: 'POST', credentials: 'same-origin', signal: controller.signal,
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({
                    mode, provider, model, prompt: prompt.trim(), ratio, quality,
                    ...(count > 1 ? { count } : {}),
                    ...(workspace?.path ? { workspaceRoot: workspace.path } : {}),
                    ...(referencesPayload === undefined ? {} : {
                        references: referencesPayload,
                    }),
                }),
            });
            const payload = await response.json();
            if (!response.ok || !('attachment' in payload))
                throw new Error('error' in payload && payload.error ? payload.error : t('generationFailed'));
            const generatedList = Array.isArray(payload.items) && payload.items.length > 0
                ? payload.items
                : [{
                        attachment: payload.attachment,
                        output: payload.output,
                        ...(payload.savedTo ? { savedTo: payload.savedTo } : {}),
                    }];
            const galleryEntries = generatedList.map((gen, idx) => ({
                id: String(gen.attachment.attachmentId || `${payload.createdAt}-${idx}`),
                attachment: gen.attachment,
                prompt: payload.prompt,
                provider: payload.provider,
                model: payload.model,
                createdAt: payload.createdAt + idx,
                aspectRatio: ratio,
                imageSize: quality,
                output: gen.output,
                ...(gen.savedTo ? { savedTo: gen.savedTo } : {}),
                ...(workspace?.path ? { workspacePath: workspace.path } : {}),
                ...(workspace?.workspaceId ? { workspaceId: workspace.workspaceId } : {}),
            }));
            if (galleryEntries.length > 1) {
                setCurrentBatch(galleryEntries);
                setSelectedBatchIds([galleryEntries[0].id]);
                setBatchKind('multi-image');
                setSelected(galleryEntries[0]);
            }
            else if (galleryEntries.length === 1) {
                setCurrentBatch(null);
                setSelectedBatchIds([]);
                setBatchKind(null);
                setSelected(galleryEntries[0]);
            }
            resetFit();
            // Mirror the generated batch onto the tldraw infinite canvas, but only
            // when the user was working there when the request started. Preview-mode
            // generations stay in the preview pane until saved: unsaved attempts
            // must not pile up on the canvas work surface.
            if (startedOnInfinite) {
                pushTlLandings(galleryEntries.map(entry => ({
                    galleryId: entry.id,
                    attachment: entry.attachment,
                    prompt: entry.prompt,
                    provider: entry.provider,
                    model: entry.model,
                })));
            }
            if (payload.failedCount && payload.failedCount > 0) {
                flash(t('partialSuccess', { success: String(generatedList.length), failed: String(payload.failedCount) }));
            }
        }
        catch (submitError) {
            if (controller.signal.aborted) {
                return;
            }
            setError(messageOf(submitError));
        }
        finally {
            if (requestRef.current === controller) {
                requestRef.current = null;
                setIsGenerating(false);
            }
        }
    };
    const isSelectedInGallery = useMemo(() => {
        if (selected === null)
            return false;
        return items.some(item => item.id === selected.id);
    }, [items, selected]);
    const selectedBatchItems = useMemo(() => {
        if (currentBatch === null)
            return [];
        const ids = new Set(selectedBatchIds);
        return currentBatch.filter(item => ids.has(item.id));
    }, [currentBatch, selectedBatchIds]);
    const pendingGalleryItems = useMemo(() => {
        const galleryIds = new Set(items.map(item => item.id));
        const targets = currentBatch === null ? (selected === null ? [] : [selected]) : selectedBatchItems;
        return targets.filter(item => !galleryIds.has(item.id));
    }, [currentBatch, items, selected, selectedBatchItems]);
    const saveButtonLabel = currentBatch !== null
        ? pendingGalleryItems.length > 0
            ? t('saveSelected', { count: String(pendingGalleryItems.length) })
            : selectedBatchItems.length === 0 ? t('saveSelected', { count: '0' }) : t('inGallery')
        : isSelectedInGallery ? t('inGallery') : t('saveToGallery');
    const saveSelectionComplete = currentBatch === null
        ? isSelectedInGallery
        : selectedBatchItems.length > 0 && pendingGalleryItems.length === 0;
    const saveGalleryEntry = async (item) => {
        let savedTo = item.savedTo;
        const targetRoot = workspace?.path || config?.workspaceRoot;
        if (!savedTo) {
            try {
                const res = await fetch(SAVE_WORKSPACE_ROUTE, {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: { 'content-type': 'application/json' },
                    body: JSON.stringify({
                        attachment: item.attachment,
                        ...(targetRoot ? { workspaceRoot: targetRoot } : {}),
                    }),
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data.savedTo)
                        savedTo = data.savedTo;
                }
            }
            catch (saveError) {
                console.warn('Failed to save to workspace:', saveError);
            }
        }
        const updatedItem = {
            ...item,
            ...(savedTo ? { savedTo } : {}),
            ...(targetRoot ? { workspacePath: targetRoot } : {}),
            ...(workspace?.workspaceId ? { workspaceId: workspace.workspaceId } : {}),
        };
        if (!(await saveGalleryItem(updatedItem))) {
            throw new Error(t('gallerySaveFailed'));
        }
        return updatedItem;
    };
    const handleSaveToGallery = async () => {
        if (selected === null)
            return;
        if (currentBatch !== null && selectedBatchItems.length === 0)
            return setError(t('needSelectResult'));
        if (pendingGalleryItems.length === 0)
            return;
        const activeId = selected.id;
        const settled = await Promise.allSettled(pendingGalleryItems.map(saveGalleryEntry));
        const saved = settled.flatMap(result => result.status === 'fulfilled' ? [result.value] : []);
        if (saved.length === 0) {
            const failed = settled.find(result => result.status === 'rejected');
            setError(failed?.status === 'rejected' ? messageOf(failed.reason) : t('generationFailed'));
            return;
        }
        const savedById = new Map(saved.map(item => [item.id, item]));
        setCurrentBatch(current => current?.map(item => savedById.get(item.id) ?? item) ?? null);
        setSelected(savedById.get(activeId) ?? saved[0]);
        setError(null);
        // Saving is the "keep this" signal: land the saved images on the infinite
        // canvas even if they were generated in preview mode (dedupe keeps
        // already-landed ones from duplicating).
        pushTlLandings(saved.map(item => ({
            galleryId: item.id,
            attachment: item.attachment,
            prompt: item.prompt,
            provider: item.provider,
            model: item.model,
        })));
        flash(saved.length > 1 ? t('savedSelected', { count: String(saved.length) }) : t('savedToGallery'));
    };
    const handleConfirmDelete = async () => {
        if (selected === null)
            return;
        const idToDelete = selected.id;
        const attachmentId = selected.attachment.attachmentId;
        const savedPath = selected.savedTo;
        setShowDeleteModal(false);
        try {
            if (deleteWorkspaceFiles && typeof savedPath === 'string' && savedPath.trim().length > 0) {
                const res = await fetch(DELETE_ROUTE, {
                    method: 'POST', credentials: 'same-origin',
                    headers: { 'content-type': 'application/json' },
                    body: JSON.stringify({ paths: [savedPath] }),
                });
                if (!res.ok) {
                    throw new Error(`删除请求网络异常 (${res.status})`);
                }
                const data = await res.json().catch(() => null);
                if (!data || data.ok === false || (Array.isArray(data.failedFiles) && data.failedFiles.length > 0)) {
                    const reason = data?.failedFiles?.[0]?.error || '工作区文件删除失败';
                    throw new Error(reason);
                }
            }
            if (isSelectedInGallery) {
                await deleteGalleryItem(idToDelete);
            }
            evictAttachmentCache(attachmentId);
            setSelectedBatchIds(current => current.filter(id => id !== idToDelete));
            if (currentBatch) {
                const remaining = currentBatch.filter(i => i.id !== idToDelete);
                if (remaining.length > 1) {
                    setCurrentBatch(remaining);
                    setSelected(remaining[0]);
                }
                else if (remaining.length === 1) {
                    setCurrentBatch(null);
                    setSelected(remaining[0]);
                }
                else {
                    setCurrentBatch(null);
                    setSelected(null);
                }
            }
            else {
                setSelected(null);
            }
            flash(t('deletedToast'));
        }
        catch (delError) {
            setError(messageOf(delError));
        }
    };
    const copySelected = async () => {
        if (image.blob === null)
            return;
        const ok = await copyImageBlob(image.blob);
        flash(ok ? t('copiedImage') : t('copyFailed'));
    };
    const downloadSelected = () => {
        if (image.url === null || selected === null)
            return;
        downloadBlobUrl(image.url, `dsh-${selected.provider}-${selected.id.slice(-8)}.${extension(selected.attachment.mediaType)}`);
    };
    const toggleFavorite = async () => {
        if (selected === null)
            return;
        const targetId = selected.id;
        if (isSelectedInGallery) {
            const nextFav = await toggleFavoriteGalleryItem(targetId);
            setSelected(curr => (curr !== null && curr.id === targetId ? { ...curr, isFavorite: nextFav } : curr));
            if (currentBatch) {
                setCurrentBatch(prev => prev ? prev.map(i => i.id === targetId ? { ...i, isFavorite: nextFav } : i) : null);
            }
            flash(nextFav ? t('favoriteAdded') : t('favoriteRemoved'));
        }
        else {
            const nextFav = !selected.isFavorite;
            if (!nextFav) {
                // Un-favoriting an unsaved preview only reverts the in-memory heart.
                setSelected(curr => (curr !== null && curr.id === targetId ? { ...curr, isFavorite: false } : curr));
                if (currentBatch) {
                    setCurrentBatch(prev => prev ? prev.map(i => i.id === targetId ? { ...i, isFavorite: false } : i) : null);
                }
                flash(t('favoriteRemoved'));
                return;
            }
            // Favoriting an unsaved preview must persist it for real: save the
            // record first, and only light the heart after the gallery write
            // succeeded (so a failed write cannot silently drop the favorite).
            try {
                const updated = await saveGalleryEntry({ ...selected, isFavorite: true });
                setSelected(curr => (curr !== null && curr.id === targetId ? updated : curr));
                if (currentBatch) {
                    setCurrentBatch(prev => prev ? prev.map(i => i.id === targetId ? updated : i) : prev);
                }
                flash(t('favoriteAdded'));
            }
            catch (err) {
                setError(err instanceof Error ? err.message : t('gallerySaveFailed'));
            }
        }
    };
    const stepLightbox = (delta) => {
        if (!currentBatch || currentBatch.length <= 1 || selected === null)
            return;
        const currIdx = currentBatch.findIndex(i => i.id === selected.id);
        if (currIdx === -1)
            return;
        const nextIdx = (currIdx + delta + currentBatch.length) % currentBatch.length;
        setSelected(currentBatch[nextIdx]);
    };
    useEffect(() => {
        if (!lightboxOpen || !currentBatch || currentBatch.length <= 1)
            return;
        const onKeyDown = (e) => {
            if (e.key === 'ArrowLeft')
                stepLightbox(-1);
            else if (e.key === 'ArrowRight')
                stepLightbox(1);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [lightboxOpen, currentBatch, selected]);
    const flash = (message) => {
        setNotice(message);
        window.setTimeout(() => setNotice(null), 2_200);
    };
    return (_jsxs("section", { ref: workbenchRootRef, className: "dsh-ig-workbench", "aria-label": t('title'), children: [_jsxs("div", { className: `dsh-ig-workbench-grid ${sidebarCollapsed ? 'is-sidebar-collapsed' : ''} ${generateCollapsed ? 'is-generate-collapsed' : ''}`, children: [!sidebarCollapsed && (_jsxs("aside", { className: "dsh-ig-recent-panel", children: [_jsxs("div", { className: "dsh-ig-panel-title", children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: '8px' }, children: [_jsx("span", { children: t('recent') }), _jsx("span", { className: "dsh-ig-count-badge", children: items.length })] }), _jsx("button", { type: "button", className: "dsh-ig-collapse-btn", onClick: () => { railToggledByUserRef.current = true; setSidebarCollapsed(true); }, title: t('collapseSidebar'), children: _jsx(PanelLeftClose, { size: 15 }) })] }), _jsxs("div", { className: "dsh-ig-recent-scroll", children: [items.length === 0 ? _jsxs("div", { className: "dsh-ig-recent-empty", children: [_jsx(ImagePlus, { size: 22 }), _jsx("span", { children: t('empty') })] }) : displayItems.map(item => _jsx(RecentItem, { item: item, active: selected?.id === item.id, onClick: () => selectItem(item) }, item.id)), items.length > visibleLimit && (_jsx("button", { type: "button", className: "dsh-ig-load-more", onClick: () => setVisibleLimit(l => l + 30), children: t('loadMore', { n: String(items.length - visibleLimit) }) }))] })] })), _jsxs("main", { className: "dsh-ig-canvas-column", children: [_jsxs("div", { className: "dsh-ig-canvas-toolbar", children: [_jsxs("div", { className: "dsh-ig-canvas-toolbar-left", children: [sidebarCollapsed && (_jsxs("button", { type: "button", onClick: () => { railToggledByUserRef.current = true; setSidebarCollapsed(false); }, title: t('expandSidebar'), children: [_jsx(PanelLeft, { size: 14 }), _jsx("span", { children: t('recent') })] })), _jsxs("div", { className: "dsh-ig-status-badge-wrap", children: [_jsxs("div", { className: "dsh-ig-status-badge", children: [_jsx("span", { className: `dsh-ig-status-dot ${configuredCount > 0 ? 'is-ready' : 'is-muted'}` }), _jsx("span", { className: "dsh-ig-status-text", children: configuredCount > 0
                                                                    ? (subscriptionSignedInCount > 0
                                                                        ? t('configuredSplit', { api: String(apiConfiguredCount), sub: String(subscriptionSignedInCount) })
                                                                        : t('configuredCount', { count: String(apiConfiguredCount) }))
                                                                    : t('unconfiguredStatus') }), _jsx(ChevronDown, { size: 11, className: "dsh-ig-status-arrow" })] }), config !== null && (_jsxs("div", { className: "dsh-ig-status-popover", children: [_jsx("div", { className: "dsh-ig-status-popover-title", children: t('providerStatus') }), _jsx("ul", { className: "dsh-ig-status-list", children: config.providers.map(p => (_jsxs("li", { className: p.configured ? 'is-configured' : 'is-missing', children: [_jsxs("div", { className: "dsh-ig-status-item-name", children: [_jsx("strong", { children: p.label }), _jsx("small", { children: p.model })] }), _jsx("span", { className: "dsh-ig-status-item-tag", children: isSubscriptionProvider(p.provider)
                                                                                ? (p.configured ? t('subSignedIn') : t('subSignedOut'))
                                                                                : (p.configured ? t('configured') : t('unconfigured')) })] }, p.provider))) })] }))] })] }), _jsxs("div", { className: "dsh-ig-canvas-toolbar-center", children: [_jsxs("button", { type: "button", className: canvasSurface === 'infinite' ? 'is-active' : '', onClick: () => {
                                                    if (canvasSurface === 'infinite') {
                                                        // Back to preview: restore the panel exactly as it was
                                                        // before the canvas folded it.
                                                        setGenerateCollapsed(generatePanelBeforeInfiniteRef.current);
                                                        setCanvasSurface('preview');
                                                        return;
                                                    }
                                                    // Into the infinite canvas: fold the generate panel so the
                                                    // surface gets the full width; reopening restores it.
                                                    generatePanelBeforeInfiniteRef.current = generateCollapsed;
                                                    setGenerateCollapsed(true);
                                                    setCanvasSurface('infinite');
                                                }, title: t('infiniteCanvas'), children: [_jsx(Frame, { size: 14 }), _jsx("span", { children: t('infiniteCanvas') })] }), canvasSurface === 'preview' && (_jsxs(_Fragment, { children: [_jsx("span", { className: "dsh-ig-toolbar-divider" }), _jsxs("button", { type: "button", className: fit ? 'is-active' : '', onClick: resetFit, children: [t('fit'), " ", _jsx(ChevronDown, { size: 13 })] }), _jsx("button", { type: "button", onClick: () => { setFit(false); setZoom(value => Math.max(25, value - 25)); }, title: "Zoom out", children: _jsx(ZoomOut, { size: 16 }) }), _jsx("span", { className: "dsh-ig-zoom-display", children: fit ? 'AUTO' : `${Math.round(zoom)}%` }), _jsx("button", { type: "button", onClick: () => { setFit(false); setZoom(value => Math.min(500, value + 25)); }, title: "Zoom in", children: _jsx(ZoomIn, { size: 16 }) })] }))] }), _jsxs("div", { className: "dsh-ig-canvas-toolbar-right", children: [canvasSurface === 'infinite' && (_jsxs("button", { type: "button", onClick: () => setShowClearCanvasModal(true), title: t('clearCanvas'), children: [_jsx(Trash2, { size: 14 }), _jsx("span", { children: t('clearCanvas') })] })), canvasSurface === 'preview' && selected !== null && (_jsxs("button", { type: "button", onClick: startNew, title: t('newGeneration'), children: [_jsx(Plus, { size: 13 }), _jsx("span", { children: t('newGeneration') })] })), canvasSurface === 'preview' && (_jsx("button", { type: "button", onClick: () => setLightboxOpen(true), title: t('fullscreen'), disabled: selected === null, children: _jsx(Expand, { size: 16 }) })), generateCollapsed && (_jsxs("button", { type: "button", onClick: () => { setGenerateCollapsed(false); }, title: t('expandGenerate'), children: [_jsx(PanelRight, { size: 14 }), _jsx("span", { children: t('generatePanel') })] }))] })] }), canvasSurface === 'infinite' && showInfiniteCanvasHint && !canvasHintDismissed && (_jsxs("div", { className: "dsh-ig-canvas-hint", children: [_jsx(Info, { size: 14 }), _jsx("span", { children: t('infiniteCanvasHint') }), _jsx("button", { type: "button", onClick: dismissCanvasHint, title: t('close'), "aria-label": t('close'), children: _jsx(X, { size: 13 }) })] })), canvasSurface === 'infinite' ? (_jsx(StudioTlCanvas, { lang: lang })) : (_jsx("div", { className: `dsh-ig-canvas ${isDragging ? 'is-dragging' : ''}`, ref: canvasRef, onMouseDown: handleCanvasMouseDown, onDoubleClick: resetFit, children: isGenerating ? (_jsxs("div", { className: "dsh-ig-generating-state", children: [_jsx("div", { className: "dsh-ig-generation-orbit", children: _jsx(Sparkles, { size: 28 }) }), _jsx("strong", { children: comparisonEnabled ? t('comparing', { count: String(comparisonTargets.length) }) : count > 1 ? t('generatingCount', { count: String(count) }) : t('generating') }), _jsx("span", { children: comparisonEnabled ? comparisonTargets.map(target => target.profile.label).join(' · ') : `${activeProfile?.label ?? ''} · ${model}` })] })) : currentBatch && currentBatch.length > 1 ? (_jsx("div", { className: "dsh-ig-canvas-cluster", "data-count": currentBatch.length, style: {
                                        transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom / 100})`,
                                        transformOrigin: 'center center',
                                        transition: isDragging ? 'none' : 'transform 0.08s ease-out',
                                    }, children: currentBatch.map((batchItem, index) => (_jsx(BatchCanvasItem, { item: batchItem, index: index, badge: batchKind === 'multi-model' ? batchItem.model : undefined, isActive: selected?.id === batchItem.id, isChecked: selectedBatchIds.includes(batchItem.id), onSelect: () => {
                                            if (!hasDraggedRef.current) {
                                                setSelected(batchItem);
                                                setSelectedBatchIds(current => current.includes(batchItem.id)
                                                    ? current.filter(id => id !== batchItem.id)
                                                    : [...current, batchItem.id]);
                                            }
                                        } }, batchItem.id))) })) : selected === null ? (_jsxs("div", { className: "dsh-ig-canvas-empty", children: [_jsx(ImagePlus, { size: 36 }), _jsx("span", { children: t('selectHistory') })] })) : image.loading ? (_jsxs("div", { className: "dsh-ig-canvas-empty", children: [_jsx(LoaderCircle, { className: "dsh-ig-spin", size: 28 }), _jsx("span", { children: t('loading') })] })) : image.url !== null ? (_jsx("img", { src: image.url, alt: selected.prompt, draggable: false, onClick: handleImageClick, style: {
                                        transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom / 100})`,
                                        transformOrigin: 'center center',
                                        transition: isDragging ? 'none' : 'transform 0.08s ease-out',
                                    } })) : (_jsxs("div", { className: "dsh-ig-canvas-empty", children: [_jsx(ImagePlus, { size: 32 }), _jsx("span", { children: t('imageLoadFailed') })] })) })), selected !== null && canvasSurface === 'preview' && _jsxs(_Fragment, { children: [_jsxs("div", { className: "dsh-ig-result-strip", children: [_jsx("span", { children: t('result') }), _jsxs("div", { children: [_jsxs("button", { type: "button", onClick: () => void continueEdit(), children: [_jsx(PencilLine, { size: 15 }), t('continueEdit')] }), _jsxs("button", { type: "button", onClick: () => { setMode('generate'); setPrompt(selected.prompt); }, children: [_jsx(RefreshCw, { size: 15 }), t('regenerate')] })] })] }), _jsxs("div", { className: "dsh-ig-result-actions", children: [_jsx("p", { children: selected.prompt }), _jsxs("div", { children: [_jsxs("button", { type: "button", className: `dsh-ig-save-gallery-btn ${saveSelectionComplete ? 'is-saved' : ''}`, onClick: () => void handleSaveToGallery(), title: saveButtonLabel, disabled: saveSelectionComplete, children: [saveSelectionComplete ? _jsx(Check, { size: 15 }) : _jsx(BookmarkPlus, { size: 15 }), _jsx("span", { children: saveButtonLabel })] }), _jsxs("button", { type: "button", onClick: () => void toggleFavorite(), title: selected.isFavorite ? t('favorited') : t('favorite'), children: [_jsx(Heart, { size: 15, fill: selected.isFavorite ? '#ef4444' : 'none', color: selected.isFavorite ? '#ef4444' : 'currentColor' }), _jsx("span", { children: selected.isFavorite ? t('favorited') : t('favorite') })] }), _jsxs("button", { type: "button", onClick: () => void copySelected(), children: [_jsx(Clipboard, { size: 15 }), _jsx("span", { children: t('copy') })] }), _jsxs("button", { type: "button", onClick: downloadSelected, children: [_jsx(Download, { size: 15 }), _jsx("span", { children: t('download') })] }), _jsxs("button", { type: "button", onClick: () => setShowDeleteModal(true), children: [_jsx(Trash2, { size: 15 }), _jsx("span", { children: t('remove') })] })] })] }), _jsxs("div", { className: "dsh-ig-result-meta", children: [_jsx("span", { children: formatRelativeTime(selected.createdAt, lang) }), _jsx("span", { children: selected.provider }), _jsx("span", { children: selected.model }), _jsx("span", { children: selected.attachment.width && selected.attachment.height ? `${selected.attachment.width} × ${selected.attachment.height}` : '—' })] })] })] }), !generateCollapsed && (_jsxs("aside", { className: "dsh-ig-generate-panel", children: [_jsxs("div", { className: "dsh-ig-panel-title", children: [_jsx("span", { children: t('generatePanel') }), _jsx("button", { type: "button", className: "dsh-ig-collapse-btn", onClick: () => { setGenerateCollapsed(true); }, title: t('collapseGenerate'), children: _jsx(PanelRightClose, { size: 15 }) })] }), _jsxs("div", { className: "dsh-ig-generator-form", children: [_jsxs("div", { className: "dsh-ig-mode-switch", children: [_jsxs("button", { type: "button", className: mode === 'generate' ? 'is-active' : '', onClick: () => setMode('generate'), children: [_jsx(Sparkles, { size: 15 }), t('generate')] }), _jsxs("button", { type: "button", className: mode === 'edit' ? 'is-active' : '', onClick: () => setMode('edit'), children: [_jsx(ImagePlus, { size: 15 }), t('edit')] })] }), mode === 'edit' && (_jsxs("div", { className: "dsh-ig-field", children: [_jsxs("div", { className: "dsh-ig-field-label", children: [_jsxs("label", { children: [t('referencesCount', { current: String(references.length), max: String(maxReferences) }), " ", _jsx("b", { children: "*" })] }), references.length > 0 && (_jsx("button", { type: "button", onClick: clearAllReferences, children: t('clear') }))] }), references.length === 0 ? (_jsxs("button", { type: "button", className: `dsh-ig-upload ${dragging ? 'is-dragging' : ''}`, onClick: () => fileInputRef.current?.click(), onDragOver: event => { event.preventDefault(); setDragging(true); }, onDragLeave: () => setDragging(false), onDrop: event => {
                                                    event.preventDefault();
                                                    setDragging(false);
                                                    addReferenceFiles(event.dataTransfer.files);
                                                }, children: [_jsx(Upload, { size: 20 }), _jsx("strong", { children: t('upload') }), _jsx("small", { children: t('uploadHint') })] })) : (_jsxs("div", { className: "dsh-ig-reference-grid", children: [references.map((item, index) => (_jsxs("div", { className: "dsh-ig-reference-card", children: [_jsx("img", { src: item.previewUrl, alt: `Ref ${index + 1}` }), _jsx("button", { type: "button", title: t('closeReference'), onClick: () => removeReference(item.id), children: _jsx(X, { size: 12 }) })] }, item.id))), references.length < maxReferences && (_jsxs("button", { type: "button", className: "dsh-ig-reference-add", onClick: () => fileInputRef.current?.click(), title: t('upload'), children: [_jsx(Upload, { size: 16 }), _jsx("span", { children: t('addMoreRef') })] }))] })), _jsx("input", { ref: fileInputRef, type: "file", multiple: true, accept: "image/png,image/jpeg,image/webp,image/gif", hidden: true, onChange: (event) => {
                                                    addReferenceFiles(event.target.files);
                                                    event.target.value = '';
                                                } })] })), _jsxs("div", { className: "dsh-ig-field", children: [_jsxs("div", { className: "dsh-ig-field-label", children: [_jsxs("label", { htmlFor: "dsh-ig-prompt", children: [t('prompt'), " ", _jsx("b", { children: "*" })] }), _jsxs("span", { children: [_jsxs("button", { type: "button", className: "dsh-ig-find-inspiration", onClick: onOpenInspiration, children: [_jsx(Sparkles, { size: 11 }), t('findInspiration')] }), _jsx("button", { type: "button", onClick: () => setPrompt(''), children: t('clear') })] })] }), _jsx("textarea", { id: "dsh-ig-prompt", value: prompt, maxLength: 2000, onChange: event => setPrompt(event.target.value), onKeyDown: event => {
                                                    if (event.nativeEvent.isComposing)
                                                        return;
                                                    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
                                                        event.preventDefault();
                                                        if (!isGenerating && config !== null && prompt.trim().length > 0)
                                                            void submit();
                                                    }
                                                }, placeholder: t('promptPlaceholder') }), _jsxs("small", { children: [prompt.length, "/2000"] })] }), config === null ? (configLoading ? (_jsx("div", { className: "dsh-ig-form-loading", children: _jsx(LoaderCircle, { className: "dsh-ig-spin", size: 18 }) })) : (_jsxs("div", { className: "dsh-ig-config-error", children: [_jsx("p", { children: configError ?? t('configLoadFailed') }), _jsxs("button", { type: "button", onClick: () => void loadConfig(), children: [_jsx(RefreshCw, { size: 13 }), _jsx("span", { children: t('retry') })] })] }))) : _jsxs(_Fragment, { children: [comparisonProfiles.length >= 2 && (_jsxs("div", { className: "dsh-ig-generation-kind", role: "group", "aria-label": t('compareModels'), children: [_jsx("button", { type: "button", className: !comparisonEnabled ? 'is-active' : '', onClick: () => toggleComparison(false), children: t('singleModel') }), _jsxs("button", { type: "button", className: comparisonEnabled ? 'is-active' : '', onClick: () => toggleComparison(true), children: [_jsx(Copy, { size: 13 }), t('compareModels')] })] })), comparisonEnabled ? _jsxs(_Fragment, { children: [_jsxs("div", { className: "dsh-ig-compare-box", children: [_jsxs("div", { className: "dsh-ig-compare-heading", children: [_jsxs("div", { children: [_jsx("strong", { children: t('compareSelect') }), _jsx("small", { children: t('compareHint') })] }), _jsx("span", { children: t('compareSelected', { count: String(comparisonTargets.length) }) })] }), _jsx("div", { className: "dsh-ig-model-checks", children: comparisonProfiles.map(item => {
                                                                    const target = comparisonTargets.find(candidate => candidate.profile.provider === item.provider);
                                                                    return (_jsxs("button", { type: "button", className: target ? 'is-selected' : '', "aria-pressed": Boolean(target), onClick: () => toggleComparisonProvider(item.provider), children: [_jsx("span", { className: "dsh-ig-model-checkmark", children: target ? _jsx(Check, { size: 12 }) : null }), _jsxs("span", { className: "dsh-ig-model-copy", children: [_jsx("strong", { children: item.label }), _jsx("small", { title: item.model, children: item.model })] }), target && _jsxs("span", { className: "dsh-ig-model-settings", children: [target.ratio, " \u00B7 ", target.quality, target.adjusted ? _jsx("em", { children: t('compareAdjusted') }) : null] })] }, item.provider));
                                                                }) }), _jsx("p", { children: t('compareParameterHint') })] }), comparisonSelectionsAllSubscription ? (_jsxs("div", { className: "dsh-ig-field-grid", children: [_jsx(FieldSelect, { label: t('ratio'), value: "auto", onChange: () => { }, options: [{ value: 'auto', label: lang === 'en' ? 'Channel default' : '通道默认' }] }), _jsx(FieldSelect, { label: t('quality'), value: "auto", onChange: () => { }, options: [{ value: 'auto', label: lang === 'en' ? 'Channel default' : '通道默认' }] })] })) : (_jsxs("div", { className: "dsh-ig-field-grid", children: [_jsx(FieldSelect, { label: t('ratio'), value: ratio, onChange: setRatio, options: comparisonRatioOptions(lang) }), _jsx(FieldSelect, { label: t('quality'), value: quality, onChange: setQuality, options: comparisonQualityOptions(lang) })] }))] }) : _jsxs(_Fragment, { children: [_jsxs("div", { className: "dsh-ig-field-grid", children: [_jsx(FieldSelect, { label: t('provider'), value: provider, onChange: changeProvider, options: config.providers.map(item => ({ value: item.provider, label: `${item.label}${item.configured ? '' : ` · ${t('unconfigured')}`}` })) }), _jsx(FieldSelect, { label: t('model'), value: model, onChange: setModel, options: activeProfile === undefined ? [] : [{ value: activeProfile.model, label: activeProfile.model }] })] }), singleProviderIsSubscription ? (_jsxs("div", { className: "dsh-ig-field-grid", children: [_jsx(FieldSelect, { label: t('ratio'), value: "auto", onChange: () => { }, options: [{ value: 'auto', label: lang === 'en' ? 'Channel default' : '通道默认' }] }), _jsx(FieldSelect, { label: t('quality'), value: "auto", onChange: () => { }, options: [{ value: 'auto', label: lang === 'en' ? 'Channel default' : '通道默认' }] })] })) : (_jsxs("div", { className: "dsh-ig-field-grid", children: [_jsx(FieldSelect, { label: t('ratio'), value: ratio, onChange: setRatio, options: localizeRatioOptions(activeProfile?.ratioOptions ?? [], lang) }), _jsx(FieldSelect, { label: t('quality'), value: quality, onChange: setQuality, options: localizeQualityOptions(activeProfile?.qualityOptions ?? [], lang) })] })), _jsxs("div", { className: "dsh-ig-field", children: [_jsx("label", { children: t('count') }), _jsx("div", { className: "dsh-ig-count-row", children: [1, 2, 3, 4].map(option => _jsx("button", { type: "button", className: `dsh-ig-count-pill ${count === option ? 'is-active' : ''}`, onClick: () => setCount(option), children: t('countUnit', { n: String(option) }) }, option)) })] })] })] }), configuredCount === 0 && config !== null && _jsx("div", { className: "dsh-ig-inline-note", children: t('noProvider') }), error !== null && _jsx("div", { className: "dsh-ig-form-error", children: error }), isGenerating ? (_jsxs("button", { type: "button", className: "dsh-ig-generate-button is-cancel", onClick: cancelGeneration, children: [_jsx(LoaderCircle, { className: "dsh-ig-spin", size: 17 }), _jsx("span", { children: t('cancelGenerate') })] })) : (_jsxs("button", { type: "button", className: "dsh-ig-generate-button", disabled: config === null, onClick: () => void submit(), children: [_jsx(Sparkles, { size: 17 }), _jsx("span", { children: comparisonEnabled ? `${t('compareStart')} (${comparisonTargets.length})` : `${t('start')}${count > 1 ? ` (${count})` : ''}` })] }))] })] }))] }), lightboxOpen && selected !== null && (_jsxs("div", { className: "dsh-ig-lightbox-backdrop", onClick: () => setLightboxOpen(false), children: [_jsxs("div", { className: "dsh-ig-lightbox-topbar", children: [_jsxs("div", { className: "dsh-ig-lightbox-meta", children: [_jsx("span", { className: "dsh-ig-tag", children: selected.provider }), _jsx("span", { className: "dsh-ig-tag dsh-ig-tag-model", children: selected.model }), currentBatch && currentBatch.length > 1 && (_jsxs("span", { className: "dsh-ig-tag", children: [currentBatch.findIndex(i => i.id === selected.id) + 1, " / ", currentBatch.length] })), selected.attachment.width && selected.attachment.height ? (_jsxs("span", { className: "dsh-ig-tag", children: [selected.attachment.width, " \u00D7 ", selected.attachment.height] })) : null] }), _jsx("button", { type: "button", className: "dsh-ig-lightbox-close-btn", title: t('close'), onClick: () => setLightboxOpen(false), children: _jsx(X, { size: 18 }) })] }), currentBatch && currentBatch.length > 1 && (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", className: "dsh-ig-lightbox-nav is-prev", onClick: e => {
                                    e.stopPropagation();
                                    stepLightbox(-1);
                                }, title: "\u4E0A\u4E00\u5F20", children: _jsx(ChevronLeft, { size: 24 }) }), _jsx("button", { type: "button", className: "dsh-ig-lightbox-nav is-next", onClick: e => {
                                    e.stopPropagation();
                                    stepLightbox(1);
                                }, title: "\u4E0B\u4E00\u5F20", children: _jsx(ChevronRight, { size: 24 }) })] })), _jsx("div", { className: "dsh-ig-lightbox-img-wrap", onClick: e => e.stopPropagation(), children: image.url !== null ? (_jsx("img", { className: "dsh-ig-lightbox-img", src: image.url, alt: selected.prompt })) : (_jsx("div", { className: "dsh-ig-lightbox-loading", children: _jsx("div", { className: "dsh-ig-lightbox-spinner" }) })) }), _jsxs("div", { className: "dsh-ig-lightbox-bottombar", onClick: e => e.stopPropagation(), children: [_jsx("div", { className: "dsh-ig-lightbox-prompt-text", title: selected.prompt, children: selected.prompt }), _jsxs("div", { className: "dsh-ig-lightbox-actions", children: [_jsxs("button", { type: "button", className: "dsh-ig-lightbox-btn", title: t('copyPpt'), onClick: async () => {
                                            await navigator.clipboard.writeText(selected.prompt);
                                            flash(t('copiedPrompt'));
                                        }, children: [_jsx(FileText, { size: 14 }), _jsx("span", { children: t('copyPpt') })] }), _jsxs("button", { type: "button", className: "dsh-ig-lightbox-btn", title: t('copy'), onClick: async () => {
                                            if (!image.blob)
                                                return;
                                            const ok = await copyImageBlob(image.blob);
                                            flash(ok ? t('copiedImage') : t('copyFailed'));
                                        }, children: [_jsx(Copy, { size: 14 }), _jsx("span", { children: t('copy') })] }), _jsxs("button", { type: "button", className: "dsh-ig-lightbox-btn", title: t('download'), onClick: downloadSelected, children: [_jsx(Download, { size: 14 }), _jsx("span", { children: t('download') })] }), _jsxs("button", { type: "button", className: `dsh-ig-lightbox-btn ${saveSelectionComplete ? 'is-saved' : ''}`, title: saveButtonLabel, onClick: () => void handleSaveToGallery(), disabled: saveSelectionComplete, children: [saveSelectionComplete ? _jsx(Check, { size: 14 }) : _jsx(BookmarkPlus, { size: 14 }), _jsx("span", { children: saveButtonLabel })] }), _jsxs("button", { type: "button", className: "dsh-ig-lightbox-btn", title: selected.isFavorite ? t('favorited') : t('favorite'), onClick: () => void toggleFavorite(), children: [_jsx(Heart, { size: 14, fill: selected.isFavorite ? '#ef4444' : 'none', color: selected.isFavorite ? '#ef4444' : 'currentColor' }), _jsx("span", { children: selected.isFavorite ? t('favorited') : t('favorite') })] })] })] })] })), showClearCanvasModal && (_jsx("div", { className: "dsh-ig-workbench-modal-backdrop", onClick: () => setShowClearCanvasModal(false), children: _jsxs("div", { className: "dsh-ig-workbench-modal-box", role: "dialog", "aria-modal": "true", "aria-label": t('clearCanvas'), onClick: event => event.stopPropagation(), children: [_jsxs("div", { className: "dsh-ig-workbench-modal-header", children: [_jsx(AlertTriangle, { size: 20, color: "#dc2626" }), _jsx("strong", { children: t('clearCanvas') })] }), _jsx("p", { className: "dsh-ig-workbench-modal-desc", children: t('clearCanvasDescription') }), _jsxs("div", { className: "dsh-ig-workbench-modal-actions", children: [_jsx("button", { autoFocus: true, type: "button", className: "dsh-ig-workbench-modal-cancel", onClick: () => setShowClearCanvasModal(false), children: t('cancel') }), _jsx("button", { type: "button", className: "dsh-ig-workbench-modal-danger", onClick: () => {
                                        try {
                                            if (!clearStudioTlCanvases()) {
                                                flash(t('loading'));
                                                return;
                                            }
                                            setShowClearCanvasModal(false);
                                            flash(t('canvasCleared'));
                                        }
                                        catch (error) {
                                            setError(messageOf(error));
                                            setShowClearCanvasModal(false);
                                        }
                                    }, children: t('clearCanvas') })] })] }) })), showDeleteModal && selected !== null && (_jsx("div", { className: "dsh-ig-workbench-modal-backdrop", onClick: () => setShowDeleteModal(false), children: _jsxs("div", { className: "dsh-ig-workbench-modal-box", onClick: e => e.stopPropagation(), children: [_jsxs("div", { className: "dsh-ig-workbench-modal-header", children: [_jsx(AlertTriangle, { size: 20, color: "#dc2626" }), _jsx("strong", { children: t('deleteModalTitle') })] }), _jsx("p", { className: "dsh-ig-workbench-modal-desc", children: t('deleteModalDesc') }), Boolean(selected.savedTo) && (_jsxs("label", { className: "dsh-ig-workbench-modal-option", children: [_jsx("input", { type: "checkbox", checked: deleteWorkspaceFiles, onChange: e => setDeleteWorkspaceFiles(e.target.checked) }), _jsx("span", { children: t('deleteWorkspaceFilesLabel') })] })), _jsxs("div", { className: "dsh-ig-workbench-modal-actions", children: [_jsx("button", { type: "button", className: "dsh-ig-workbench-modal-cancel", onClick: () => setShowDeleteModal(false), children: t('cancel') }), _jsx("button", { type: "button", className: "dsh-ig-workbench-modal-danger", onClick: () => void handleConfirmDelete(), children: t('confirm') })] })] }) })), notice !== null && _jsx("div", { className: "dsh-ig-workbench-toast", children: notice })] }));
};
const BatchCanvasItem = ({ item, index, badge, isActive, isChecked, onSelect }) => {
    const image = useAttachmentImage(item.attachment, true);
    return (_jsxs("div", { className: `dsh-ig-canvas-item ${isActive ? 'is-active' : ''} ${isChecked ? 'is-checked' : ''}`, onClick: onSelect, children: [_jsx("span", { className: `dsh-ig-canvas-badge ${badge ? 'is-model' : ''}`, title: badge, children: badge ?? `#${index + 1}` }), _jsx("span", { className: "dsh-ig-canvas-check", "aria-hidden": "true", children: isChecked ? _jsx(Check, { size: 14 }) : null }), image.url !== null ? (_jsx("img", { src: image.url, alt: item.prompt, draggable: false })) : image.loading ? (_jsx("div", { className: "dsh-ig-canvas-item-loading", children: _jsx(LoaderCircle, { className: "dsh-ig-spin", size: 24 }) })) : (_jsx("div", { className: "dsh-ig-canvas-item-loading", children: _jsx(ImagePlus, { size: 24 }) }))] }));
};
const FieldSelect = ({ label, value, options, onChange }) => _jsxs("label", { className: "dsh-ig-field-select", children: [_jsx("span", { children: label }), _jsx("select", { value: value, onChange: event => onChange(event.target.value), children: options.map(option => _jsx("option", { value: option.value, children: option.label }, option.value)) })] });
const LOCALIZED_RATIOS = {
    auto: { zh: '自动', en: 'Auto' },
    '1:1': { zh: '1:1 方形', en: '1:1 Square' },
    '3:2': { zh: '3:2 横向', en: '3:2 Landscape' },
    '2:3': { zh: '2:3 肖像', en: '2:3 Portrait' },
    '4:3': { zh: '4:3 横向', en: '4:3 Landscape' },
    '3:4': { zh: '3:4 竖向', en: '3:4 Portrait' },
    '16:9': { zh: '16:9 宽屏', en: '16:9 Widescreen' },
    '9:16': { zh: '9:16 竖屏', en: '9:16 Portrait' },
};
const LOCALIZED_QUALITIES = {
    standard: { zh: '标准（推荐）', en: 'Standard (Recommended)' },
    auto: { zh: '模型自动', en: 'Model Auto' },
};
function localizeRatioOptions(options, lang) {
    return options.map(opt => ({
        value: opt.value,
        label: LOCALIZED_RATIOS[opt.value]?.[lang] ?? opt.label,
    }));
}
function localizeQualityOptions(options, lang) {
    return options.map(opt => ({
        value: opt.value,
        label: LOCALIZED_QUALITIES[opt.value]?.[lang] ?? opt.label,
    }));
}
function comparisonRatioOptions(lang) {
    return ['1:1', '3:2', '2:3', '16:9', '9:16'].map(value => ({
        value,
        label: LOCALIZED_RATIOS[value]?.[lang] ?? value,
    }));
}
function comparisonQualityOptions(lang) {
    return [
        { value: 'standard', label: lang === 'zh' ? '标准' : 'Standard' },
        { value: '1K', label: '1K' },
        { value: '2K', label: '2K' },
        { value: '4K', label: '4K' },
    ];
}
const RecentItem = ({ item, active, onClick }) => {
    const [setRef, inView] = useInView();
    const image = useAttachmentImage(item.attachment, inView);
    return (_jsx("button", { ref: setRef, type: "button", className: `dsh-ig-recent-item ${active ? 'is-active' : ''}`, onClick: onClick, title: item.prompt, children: _jsx("div", { className: "dsh-ig-recent-thumb", children: image.url !== null ? _jsx("img", { src: image.url, alt: "", loading: "lazy" }) : _jsx(ImagePlus, { size: 18 }) }) }));
};
function useInView() {
    const [inView, setInView] = useState(false);
    const observerRef = useRef(null);
    useEffect(() => {
        return () => {
            observerRef.current?.disconnect();
        };
    }, []);
    const refCallback = useCallback((el) => {
        observerRef.current?.disconnect();
        if (!el || inView)
            return;
        if (typeof IntersectionObserver === 'undefined') {
            setInView(true);
            return;
        }
        const observer = new IntersectionObserver(([entry]) => {
            if (entry?.isIntersecting) {
                setInView(true);
                observer.disconnect();
            }
        }, { rootMargin: '120px' });
        observerRef.current = observer;
        observer.observe(el);
    }, [inView]);
    return [refCallback, inView];
}
export function useAttachmentImage(attachment, shouldLoad = true) {
    const [value, setValue] = useState({ url: null, blob: null, loading: false });
    useEffect(() => {
        if (attachment === undefined || !shouldLoad) {
            setValue({ url: null, blob: null, loading: false });
            return;
        }
        let cancelled = false;
        let objectUrl = null;
        setValue({ url: null, blob: null, loading: true });
        fetchAttachmentBlob(attachment)
            .then(blob => {
            if (cancelled)
                return;
            objectUrl = URL.createObjectURL(blob);
            setValue({ url: objectUrl, blob, loading: false });
        })
            .catch(() => { if (!cancelled)
            setValue({ url: null, blob: null, loading: false }); });
        return () => {
            cancelled = true;
            if (objectUrl !== null)
                URL.revokeObjectURL(objectUrl);
        };
    }, [attachment?.attachmentId, shouldLoad]);
    return value;
}
export function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result;
            const commaIndex = result.indexOf(',');
            resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
        };
        reader.onerror = () => reject(reader.error ?? new Error('Failed to read image file'));
        reader.readAsDataURL(file);
    });
}
function extension(mediaType) {
    return mediaType === 'image/jpeg' ? 'jpg' : mediaType.split('/')[1] ?? 'png';
}
function messageOf(error) {
    return error instanceof Error ? error.message : String(error);
}
//# sourceMappingURL=studio-view.js.map